import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
} from "@mui/material";
import {
  PasswordDataType,
  RepresentativeFormType,
} from "../../types/entitiesModel";

interface RepresentativeFormNoSendType {
  formState: RepresentativeFormType;
  setFormStateStudent: (value: RepresentativeFormType) => void;
  handleChangeForm: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  repExists: boolean;
  setRepExists: (value: boolean) => void;
  importantAddRep: boolean;
  addPasswordField: boolean;
  passwordState?: PasswordDataType | null;
  setPasswordData?: (value: PasswordDataType) => void | null;
}

const RepresentativeFormNoSend: React.FC<RepresentativeFormNoSendType> = (
  props: RepresentativeFormNoSendType
) => {
  const handleToggleRepExists = () => {
    props.setRepExists(!props.repExists);
  };

  const [passwordMatch, setPasswordMatch] = React.useState<boolean | null>(
    null
  );

  const handlePasswordChange =
    (field: keyof PasswordDataType) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newPasswordState = {
        ...props.passwordState,
        [field]: e.target.value,
      } as PasswordDataType;

      // Actualizar el estado de las contraseñas
      if (props.setPasswordData) {
        props.setPasswordData(newPasswordState);
      }

      // Comparar contraseñas cuando se escribe en confirmPassword
      if (field === "confirmPassword" && props.passwordState?.password) {
        setPasswordMatch(
          newPasswordState.password === newPasswordState.confirmPassword
        );
      } else if (field === "password") {
        // Resetear la comparación si se modifica el campo de password
        setPasswordMatch(null);
      }
    };

  return (
    <React.Fragment>
      <Grid container spacing={0}>
        <Grid item lg={12} md={12} xs={12}>
          <div>
            <Card
              variant="outlined"
              sx={{
                p: 0,
                backgroundColor:
                  props.repExists || props.importantAddRep
                    ? "white"
                    : "#f7f7f7",
              }}
            >
              <Box
                sx={{
                  padding: "15px 30px",
                }}
                display="flex"
                alignItems="center"
              >
                <Box flexGrow={1}>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: "500",
                    }}
                  >
                    Información del representante
                  </Typography>
                </Box>

                {!props.importantAddRep ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={props.repExists}
                        onChange={handleToggleRepExists}
                        color="primary"
                      />
                    }
                    label="¿Añadir un representante?"
                    sx={{
                      ml: 2,
                    }}
                  />
                ) : null}
              </Box>
              <Divider />
              <CardContent
                sx={{
                  padding: "30px",
                }}
              >
                <form>
                  {/* Sección de Información del Representante */}
                  <Grid container spacing={2}>
                    <Grid item md={6} xs={12}>
                      <TextField
                        id="name"
                        label="Nombre"
                        variant="outlined"
                        placeholder="Nombre"
                        fullWidth
                        sx={{
                          mb: 2,
                        }}
                        name="name"
                        onChange={props.handleChangeForm}
                        disabled={!props.repExists && !props.importantAddRep}
                      />
                      <TextField
                        id="document"
                        label="Documento"
                        type="text"
                        variant="outlined"
                        placeholder="Documento"
                        fullWidth
                        sx={{
                          mb: 2,
                        }}
                        name="document"
                        onChange={props.handleChangeForm}
                        disabled={!props.repExists && !props.importantAddRep}
                      />
                      <TextField
                        id="email"
                        label="Email"
                        type="text"
                        variant="outlined"
                        placeholder="Email"
                        fullWidth
                        sx={{
                          mb: 2,
                        }}
                        name="email"
                        onChange={props.handleChangeForm}
                        disabled={!props.repExists && !props.importantAddRep}
                      />
                    </Grid>

                    <Grid item md={6} xs={12}>
                      <TextField
                        id="relationship"
                        label="Relación con estudiante"
                        type="text"
                        variant="outlined"
                        placeholder="Relación con estudiante"
                        fullWidth
                        sx={{
                          mb: 2,
                        }}
                        name="relationship"
                        onChange={props.handleChangeForm}
                        disabled={!props.repExists && !props.importantAddRep}
                      />
                      <TextField
                        id="phone"
                        label="Teléfono"
                        type="text"
                        variant="outlined"
                        placeholder="Teléfono"
                        fullWidth
                        sx={{
                          mb: 2,
                        }}
                        name="phone"
                        onChange={props.handleChangeForm}
                        disabled={!props.repExists && !props.importantAddRep}
                      />
                    </Grid>
                  </Grid>

                  {/* Sección de Contraseña */}
                  {props.addPasswordField && (
                    <Box>
                      <Divider sx={{ my: 3 }} />
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: "500",
                          mb: 2,
                        }}
                      >
                        Crear contraseña para su usuario en el sistema
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            id="password"
                            label="Contraseña"
                            type="password"
                            variant="outlined"
                            placeholder="Contraseña"
                            fullWidth
                            name="password"
                            onChange={handlePasswordChange("password")}
                            disabled={
                              !props.repExists && !props.importantAddRep
                            }
                            autoComplete="password"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            id="confirmPassword"
                            label="Confirmar Contraseña"
                            type="password"
                            variant="outlined"
                            placeholder="Confirmar Contraseña"
                            fullWidth
                            name="confirmPassword"
                            onChange={handlePasswordChange("confirmPassword")}
                            disabled={
                              !props.repExists && !props.importantAddRep
                            }
                            error={passwordMatch === false}
                            sx={{
                              borderColor: passwordMatch
                                ? "green" 
                                : passwordMatch === false
                                ? "red" 
                                : "inherit",
                            }}
                            autoComplete="confirmPassword"
                          />
                        </Grid>
                      </Grid>
                      {/* Mensaje de confirmación de contraseñas */}
                      {passwordMatch !== null && (
                        <Alert severity={passwordMatch ? "success" : "error"} sx={{mt: 2}}>
                          <Typography
                            sx={{
                              color: passwordMatch ? "green" : "red",
                              fontSize: "14px",
                            }}
                          >
                            {passwordMatch
                              ? "Las contraseñas coinciden."
                              : "Las contraseñas no coinciden."}
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

export default RepresentativeFormNoSend;
